import { select } from 'd3-selection'
import * as Legend from '../legend'
import { appendPatternFillLegend } from '../legend-pattern-fill'
import { drawSizeLegend } from './legend-symbol-size'
import { drawDiscreteLegend, buildDiscreteLabelFormatter } from '../legend-discrete'
import { getCentroidsGroup } from '../../core/geo/centroids'
//types
/** @typedef {import('../../types/core/MapInstance').MapInstance} MapInstance */

/**
 * A legend for proportional symbol map
 *
 * @param {*} map
 */
export const legend = function (map, config) {
    //build generic legend object for the map
    const out = Legend.legend(map)

    //size legend config (legend illustrating the values of different symbol sizes)
    out.sizeLegend = {
        title: null,
        titlePadding: 15, //padding between title and legend body
        values: undefined, //manually define raw data values
        cellNb: 3, //number of elements in the legend
        shapePadding: 15, //the y distance between consecutive legend shape elements
        shapeOffsets: { x: 0, y: 0 },
        shapeFill: 'white',
        shapeStroke: null,
        shapeStrokeWidth: null,
        labelOffsets: { x: 10, y: 0 }, //the distance between the legend box elements to the corresponding text label
        decimals: 0, //the number of decimal for the legend labels
        labelFormatter: undefined,
        labels: null, // user-defined labels for each size
        noData: false, // show no data legend item
        noDataText: 'No data', //no data text label
        _totalBarsHeight: 0,
        _totalD3SymbolsHeight: 0,
    }

    // color legend config (legend illustrating the data-driven colour classes)
    out.colorLegend = {
        title: null,
        titlePadding: 10, //padding between title and legend body
        marginTop: 30, // margin top (distance between color and size legend)
        shapeWidth: out.shapeWidth, //the width of the legend box elements
        shapeHeight: out.shapeHeight, //the height of the legend box elements
        shapePadding: 1, //the distance between consecutive legend shape elements in the color legend
        labelOffsets: { x: 3, y: 0 }, //distance (x) between label text and its corresponding shape element
        decimals: undefined, //the number of decimal for the legend labels (undefined for auto-detect based on data)
        labelFormatter: undefined, // user-defined d3 format function
        labelType: 'thresholds', // type of labels to show: thresholds or ranges
        labels: null, // user-defined labels for each class
        noData: true, //show no data
        noDataText: 'No data', //no data text label
        sepLineLength: out.sepLineLength, // //the separation line length
        sepLineStroke: 'black', //the separation line color
        sepLineStrokeWidth: 1, //the separation line width
        tickLength: 4, // threshold ticks length in px
        //diverging line for diverging color legends
        pointOfDivergenceLabel: undefined,
        pointOfDivergence: undefined,
        pointOfDivergencePadding: 7,
        divergingLineLength: undefined,
        divergingArrowLength: undefined,
    }

    //override attribute values with config values
    if (config)
        for (let key in config) {
            if (key == 'colorLegend' || key == 'sizeLegend') {
                for (let p in out[key]) {
                    //override each property in size and color legend configs
                    if (config[key][p] !== undefined) {
                        out[key][p] = config[key][p]
                    }
                }
                if (config.colorLegend == false) out.colorLegend = false
            } else {
                out[key] = config[key]
            }
        }

    //@override
    out.update = function () {
        out.updateConfig()
        out.updateContainer()

        if (out.lgg.node()) {
            const lgg = out.lgg

            //remove previous content
            lgg.selectAll('*').remove()

            //draw legend background box
            out.makeBackgroundBox()
            if (out.title) out.addTitle()
            if (out.subtitle) out.addSubtitle()

            // reset height counters
            out.sizeLegend._totalBarsHeight = 0
            out.sizeLegend._totalD3SymbolsHeight = 0

            // initial x and y positions for the internal legend elements
            const baseY = out.getBaseY()
            const baseX = out.getBaseX()

            // legend for size
            if (map.classifierSize_) {
                drawSizeLegend(out, baseX, baseY)
            }
            if (map.classifierColor_ && out.colorLegend) {
                let x = baseX
                let y = baseY
                if (out._sizeLegendContainer) {
                    // position it below size legend ( + out.colorLegend.marginTop)
                    y += out._sizeLegendContainer.node().getBBox().height + out.colorLegend.marginTop
                }
                drawDiscreteLegend(out, x, y)
            }

            // Append pattern fill legend items BELOW the main legend
            if (out.map.patternFill_) {
                const legendHeight = out.lgg.node().getBBox().height
                const patternContainer = out.lgg
                    .append('g')
                    .attr('class', 'pattern-fill-legend')
                    .attr('transform', `translate(${baseX}, ${legendHeight + out.boxPadding + 5})`)
                appendPatternFillLegend(out, patternContainer)
            }

            //set legend box dimensions
            out.setBoxDimension()
        }
    }

    return out
}

function getPsColorThresholds(out) {
    const map = out.map
    const thresholds =
        map.psThresholds_.length > 1
            ? map.psThresholds_
            : Array.from({ length: map.psClasses_ })
                  .map((_, index) => {
                      return map.classifier().invertExtent(index)[out.ascending ? 0 : 1]
                  })
                  .slice(1) // Remove the first entry and return the rest as an array
    return thresholds
}

export function getPropSymbolColorLabelFormatter(out) {
    const stat = out.getColorStats(out)
    const labelType = out.colorLegend?.labelType || 'thresholds'
    const userFormatter = out.colorLegend?.labelFormatter

    return buildDiscreteLabelFormatter(out, () => getPsColorThresholds(out), stat, labelType, userFormatter)
}

// Highlight selected regions on mouseover
export function highlightPsSymbols(map, ecl) {
    const allSymbols = getCentroidsGroup(map).selectAll('[ecl]')

    // Set all symbols to visible
    allSymbols.each(function (d, i) {
        let symbol = select(this)
        symbol.style('opacity', '0')
    })

    // Highlight only the selected regions by restoring their original opacity
    const selectedSymbols = allSymbols.filter("[ecl='" + ecl + "']")
    selectedSymbols.each(function (d, i) {
        let symbol = select(this)
        symbol.style('opacity', map.psFillOpacity_) // Restore original opacity for selected regions
    })
}

// Reset all regions to their original opacitys on mouseout
export function unhighlightPsSymbols(map) {
    const allSymbols = getCentroidsGroup(map).selectAll('[ecl]')

    // Restore each region's original opacity from the fill___ attribute
    allSymbols.each(function (d, i) {
        let symbol = select(this)
        symbol.style('opacity', map.psFillOpacity_) // Restore original opacity for selected regions
    })
}
